import { useId, type ReactNode } from "react";
import { Link } from "react-router";
import type { CatalogItemDto, CatalogShop, HomeLook, HomeStageOfferDto } from "@townsy/contracts";
import type { LayoutOutcome } from "../../homes/commands";
import { cardPhrase, withDomainBlock, type DollarGate } from "../../commerce/gates";
import {
  ANCHOR_TYPE_SHORT,
  homeDollarBlock,
  itemBuyAvailability,
  ownedCount,
  stageLabel,
  stageLadder,
  upgradeAvailability,
  type Availability,
  type HomeBlockCode,
} from "../../homes/homeLogic";
import { exteriorLookSummary, interiorLookSummary } from "../../homes/lookPalette";
import { formatCents } from "../../lib/format";
import { formatCoins } from "../../lib/wallet";
import { HomeExteriorPreview } from "./HomePreview";
import { LookEditor } from "./LookEditor";
import type { HomeData } from "./useHomeData";
import { useLivePayments, useTestPrices } from "../testPrices";

/**
 * Townsy Home Builders (home level + yard items + paint & style) and Townsy Furnishings (furniture +
 * inside style), as one presentational view shared by the in-town panels and the 2D `/directory`
 * (docs/01 sections 6-7). Buying always goes through a review dialog (opened by the caller); every
 * disabled button says exactly why. Dollar offers go through the checkout (Townsy Credit first, then a
 * card in Stripe test mode) and are explicitly disabled, with the reason, when payments are off.
 */
export const SHOP_INFO: Record<CatalogShop, { name: string; address: string; tagline: string }> = {
  home_builders: { name: "Townsy Home Builders", address: "2 Plaza Way", tagline: "Home upgrades and yard features" },
  furnishings: { name: "Townsy Furnishings", address: "3 Plaza Way", tagline: "Furniture and inside style" },
};

/** Reasons that apply to every item alike are shown once, above the list. */
const SHARED_BLOCKS = new Set<HomeBlockCode>(["SIGN_IN_REQUIRED", "EMAIL_NOT_VERIFIED", "FEATURE_DISABLED", "HOME_LOADING", "HOME_UNAVAILABLE", "HOME_REQUIRED"]);

export interface HomeShopViewProps {
  shop: CatalogShop;
  data: HomeData;
  onBuy(item: CatalogItemDto): void;
  onUpgrade(next: HomeStageOfferDto): void;
  /** Card + Townsy Credit availability for this visitor (payments config + viewer). */
  dollar: DollarGate;
  onBuyUsd(item: CatalogItemDto): void;
  onUpgradeUsd(next: HomeStageOfferDto): void;
  onSaveLook(look: HomeLook): Promise<LayoutOutcome>;
  /** Open the My home manager (null hides the shortcut). */
  onOpenMyHome: ((tab: "yard" | "interior") => void) | null;
  /** Where to buy a plot (a link or fast-travel button), shown when the resident has no home. */
  realEstateAction: ReactNode;
}

export function HomeShopView({ shop, data, onBuy, onUpgrade, onSaveLook, onOpenMyHome, realEstateAction, dollar, onBuyUsd, onUpgradeUsd }: HomeShopViewProps) {
  const { home, viewer } = data;
  const testPrices = useTestPrices();
  const lookBlocked = viewer.isGuest
    ? "Sign in and own a home to save a look."
    : data.homeState === "loading"
      ? "Checking your home…"
      : !home
        ? "Buy a plot first at Townsy Real Estate to save a look."
        : null;
  return (
    <div className="stack" data-testid={`shop-${shop}`}>
      {shop === "home_builders" ? (
        <YourHomeSection
          data={data}
          onUpgrade={onUpgrade}
          onOpenMyHome={onOpenMyHome}
          realEstateAction={realEstateAction}
          dollar={dollar}
          onUpgradeUsd={onUpgradeUsd}
        />
      ) : (
        <HomeLine data={data} onOpenMyHome={onOpenMyHome} realEstateAction={realEstateAction} />
      )}
      <ItemsSection shop={shop} data={data} onBuy={onBuy} dollar={dollar} onBuyUsd={onBuyUsd} />
      <section className="section" aria-label={shop === "home_builders" ? "Paint and style" : "Inside style"}>
        <div className="section-title">{shop === "home_builders" ? "Paint & style" : "Inside style"}</div>
        {home ? (
          <p className="small muted" style={{ margin: "0 0 6px" }}>
            Now: {shop === "home_builders" ? exteriorLookSummary(home.look) : interiorLookSummary(home.look)}
          </p>
        ) : null}
        <LookEditor
          part={shop === "home_builders" ? "exterior" : "interior"}
          look={home?.look ?? null}
          stage={home?.stage ?? null}
          saveBlockedReason={lookBlocked}
          onSave={onSaveLook}
        />
      </section>
      <p className="hint" style={{ margin: 0 }}>
        {testPrices ? "Development test prices. " : null}A coin price and a dollar price are two separate offers, not an exchange rate. Coins and Townsy Credit are
        separate balances.
      </p>
    </div>
  );
}

function GuestNotice({ what }: { what: string }) {
  return (
    <div className="notice notice-info small">
      Guests can look around. <Link to="/account">Sign in or create an account</Link> to {what}.
    </div>
  );
}

function YourHomeSection({
  data,
  onUpgrade,
  onOpenMyHome,
  realEstateAction,
  dollar,
  onUpgradeUsd,
}: Pick<HomeShopViewProps, "data" | "onUpgrade" | "onOpenMyHome" | "realEstateAction" | "dollar" | "onUpgradeUsd">) {
  const { home, catalog, viewer } = data;
  const ladder = stageLadder(catalog?.stages ?? [], home?.stage ?? null);
  const plan = upgradeAvailability(data.inputs);
  const usd = withDomainBlock(dollar, homeDollarBlock(data.inputs, "upgrade"));
  const card = cardPhrase(useLivePayments() ? "live" : "test");
  const reasonId = useId();
  const usdReasonId = useId();
  return (
    <section className="section" style={{ marginTop: 0 }} aria-label="Your home" data-testid="your-home">
      <div className="section-title">Your home</div>
      {viewer.isGuest ? (
        <GuestNotice what="own and upgrade a home" />
      ) : data.homeState === "loading" ? (
        <p className="muted small">Loading your home…</p>
      ) : data.homeState === "error" ? (
        <div className="notice notice-danger small" role="alert">
          Your home could not be loaded. Try again in a moment.
        </div>
      ) : !home ? (
        <div className="notice notice-info small" data-testid="no-home">
          You don't own a plot yet. Buy one at Townsy Real Estate, 1 Plaza Way: it starts as a tent, and you upgrade it here. {realEstateAction}
        </div>
      ) : (
        <dl className="kv">
          <dt>Address</dt>
          <dd>{home.address}</dd>
          <dt>Level</dt>
          <dd data-testid="home-stage">{stageLabel(home.stage)}</dd>
          <dt>Look</dt>
          <dd className="small">{exteriorLookSummary(home.look)}</dd>
        </dl>
      )}

      <ol className="stage-ladder" aria-label="Home levels" data-testid="stage-ladder">
        {ladder.map((step) => (
          <li key={step.stage} className={`stage-step stage-${step.status}`} aria-current={step.status === "current" ? "step" : undefined}>
            <span className="stage-name">{step.name}</span>
            <span className="stage-price">
              {step.rank === 0 ? "with the plot" : step.offer?.coins !== null && step.offer?.coins !== undefined ? formatCoins(step.offer.coins) : "—"}
            </span>
            {step.status === "current" ? <span className="visually-hidden"> (your home now)</span> : null}
            {step.status === "next" ? <span className="visually-hidden"> (next upgrade)</span> : null}
          </li>
        ))}
      </ol>

      {home ? (
        plan.next ? (
          <div className="upgrade-card" data-testid="next-upgrade">
            <HomeExteriorPreview stage={plan.next.stage} look={home.look} size={112} label={`Preview of the next level: ${plan.next.name}`} />
            <div className="stack" style={{ gap: 6, minWidth: 0 }}>
              <div>
                Next level: <strong data-testid="next-stage-name">{plan.next.name}</strong>
              </div>
              <div className="row" style={{ gap: 6 }}>
                {plan.priceCoins !== null ? (
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    disabled={!plan.enabled}
                    aria-describedby={plan.reason ? reasonId : undefined}
                    onClick={() => plan.next && onUpgrade(plan.next)}
                    data-testid="upgrade-coins"
                  >
                    Upgrade for {formatCoins(plan.priceCoins)}
                  </button>
                ) : null}
                {plan.priceUsdCents !== null ? (
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={!usd.enabled}
                    title={usd.reason ?? `Townsy Credit first, then ${card}`}
                    aria-describedby={usdReasonId}
                    onClick={() => plan.next && onUpgradeUsd(plan.next)}
                    data-testid="upgrade-usd"
                  >
                    Upgrade for {formatCents(plan.priceUsdCents)}
                  </button>
                ) : null}
              </div>
              {plan.reason ? (
                <span id={reasonId} className="hint" data-testid="upgrade-reason">
                  {plan.reason}
                </span>
              ) : null}
              {plan.priceUsdCents !== null ? (
                <span id={usdReasonId} className="hint" data-testid="upgrade-usd-reason">
                  {usd.enabled ? `Dollar price: Townsy Credit first, then ${card}.` : `Dollar price: ${usd.reason}`}
                </span>
              ) : null}
            </div>
          </div>
        ) : (
          <p className="small" data-testid="top-stage" style={{ margin: 0 }}>
            Your home is a castle: the top level.
          </p>
        )
      ) : null}
      <p className="hint" style={{ margin: 0 }}>
        Upgrades go one level at a time and charge only the next level's price. Homes can never be taken over by other players.
      </p>
      {home && onOpenMyHome ? (
        <button type="button" className="btn btn-sm" onClick={() => onOpenMyHome("yard")} data-testid="open-my-home">
          Customize my home
        </button>
      ) : null}
    </section>
  );
}

/** Furnishings: a one-line reminder of whose home the furniture goes to. */
function HomeLine({ data, onOpenMyHome, realEstateAction }: Pick<HomeShopViewProps, "data" | "onOpenMyHome" | "realEstateAction">) {
  const { home, viewer } = data;
  return (
    <section className="section" style={{ marginTop: 0 }} aria-label="Your home" data-testid="your-home">
      {viewer.isGuest ? (
        <GuestNotice what="furnish a home" />
      ) : data.homeState === "loading" ? (
        <p className="muted small">Loading your home…</p>
      ) : !home ? (
        <div className="notice notice-info small" data-testid="no-home">
          You need a home to furnish. Buy a plot at Townsy Real Estate, 1 Plaza Way. {realEstateAction}
        </div>
      ) : (
        <div className="row" style={{ justifyContent: "space-between" }}>
          <span className="small">
            Your home: <strong>{home.address}</strong> · {stageLabel(home.stage)} · {interiorLookSummary(home.look)}
          </span>
          {onOpenMyHome ? (
            <button type="button" className="btn btn-sm" onClick={() => onOpenMyHome("interior")} data-testid="open-my-home">
              Customize my home
            </button>
          ) : null}
        </div>
      )}
    </section>
  );
}

function ItemsSection({ shop, data, onBuy, dollar, onBuyUsd }: Pick<HomeShopViewProps, "shop" | "data" | "onBuy" | "dollar" | "onBuyUsd">) {
  const sharedId = useId();
  const usdId = useId();
  const usd = withDomainBlock(dollar, homeDollarBlock(data.inputs, "item"));
  const card = cardPhrase(useLivePayments() ? "live" : "test");
  const items = (data.catalog?.items ?? []).filter((i) => i.shop === shop);
  const shared: Availability | null = items[0] ? itemBuyAvailability({ ...data.inputs, offer: items[0].offer }) : null;
  const sharedReason = shared?.code && SHARED_BLOCKS.has(shared.code) ? shared.reason : null;
  return (
    <section className="section" aria-label={shop === "home_builders" ? "Yard and garden" : "Furniture"}>
      <div className="section-title">{shop === "home_builders" ? "Yard & garden" : "Furniture"}</div>
      {data.signedIn && data.wallets ? (
        <p className="small muted" style={{ margin: "0 0 6px" }}>
          Your coins: <strong className="coin-text">{formatCoins(data.wallets.coins.available)}</strong>. Bought items go to your storage; place
          them from My home.
        </p>
      ) : null}
      {sharedReason ? (
        <div id={sharedId} className="notice notice-info small" data-testid={`items-reason-${shop}`}>
          {sharedReason}
        </div>
      ) : null}
      {data.catalogState === "loading" ? (
        <p className="muted">Loading the catalog…</p>
      ) : data.catalogState === "error" ? (
        <div className="notice notice-danger" role="alert">
          Couldn't load the catalog: {data.catalogError}{" "}
          <button type="button" className="btn btn-sm" onClick={data.refetchCatalog}>
            Retry
          </button>
        </div>
      ) : items.length === 0 ? (
        <p className="muted">Nothing is for sale here right now.</p>
      ) : (
        <ul className="listing-list" data-testid={`shop-items-${shop}`}>
          {items.map((item) => {
            const avail = itemBuyAvailability({ ...data.inputs, offer: item.offer });
            const ownReason = avail.reason && !(avail.code && SHARED_BLOCKS.has(avail.code)) ? avail.reason : null;
            const reasonId = `${sharedId}-${item.itemId}`;
            const { owned, stored } = ownedCount(data.inventory, item.itemId);
            const fits = [...new Set(item.anchorTypes.map((t) => ANCHOR_TYPE_SHORT[t]))].join(" or ");
            return (
              <li key={item.itemId} className="listing-item" data-testid={`item-${item.itemId}`}>
                <div className="listing-head">
                  <div style={{ minWidth: 0 }}>
                    <strong>{item.name}</strong>
                    <div className="small muted">{item.description}</div>
                    <div className="small muted">Fits: {fits}</div>
                  </div>
                  {owned > 0 ? (
                    <span className="badge badge-success" data-testid={`owned-${item.itemId}`}>
                      You own {owned}
                      {stored > 0 ? ` · ${stored} in storage` : ""}
                    </span>
                  ) : null}
                </div>
                <div className="row" style={{ gap: 6 }}>
                  {item.offer.coins !== null ? (
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={!avail.enabled}
                      aria-describedby={ownReason ? reasonId : sharedReason ? sharedId : undefined}
                      onClick={() => onBuy(item)}
                      data-testid={`buy-item-${item.itemId}`}
                    >
                      Buy for {formatCoins(item.offer.coins)}
                    </button>
                  ) : null}
                  {item.offer.usdCents !== null ? (
                    <button
                      type="button"
                      className="btn btn-sm"
                      disabled={!usd.enabled}
                      title={usd.reason ?? `Townsy Credit first, then ${card}`}
                      aria-describedby={usdId}
                      onClick={() => onBuyUsd(item)}
                      data-testid={`buy-item-usd-${item.itemId}`}
                    >
                      Buy for {formatCents(item.offer.usdCents)}
                    </button>
                  ) : null}
                </div>
                {ownReason ? (
                  <span id={reasonId} className="hint" data-testid={`item-reason-${item.itemId}`}>
                    {ownReason}
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      {items.some((i) => i.offer.usdCents !== null) ? (
        <p id={usdId} className="hint" style={{ margin: "6px 0 0" }} data-testid={`items-usd-reason-${shop}`}>
          {usd.enabled ? `Dollar prices: Townsy Credit first, then ${card}.` : `Dollar prices: ${usd.reason}`}
        </p>
      ) : null}
    </section>
  );
}
